import { printDocument } from "../../utils/export";
import { formatDate, formatMoney } from "../../utils/format";
import { COMPANY, LOGO_URL } from "../../services/config";
import { generateInvoice } from "../../services/invoice";

// The printable invoice ("Order & Shipment Details"), one definition for every way of printing it. It prints a
// generated invoice (services/invoice.js): its number, the customer, one line per order with the shipment charges
// and the TOTAL row - exactly what was kept when it was generated, so the history reprints the same figures.

const esc = (value) => String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const money = (value) => formatMoney(+value || 0);
const weight = (value) => (+value ? (+value).toFixed(3).replace(/\.?0+$/, "") : "0");
const absoluteUrl = (url) => (/^(https?:|data:)/.test(url) ? url : `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`);

export const invoiceHtml = (invoice) => {
  const lines = invoice.lines || [];
  const logo = LOGO_URL ? `<img class="logo" src="${esc(absoluteUrl(LOGO_URL))}" alt="${esc(COMPANY.name)}" />` : "";

  const rows = lines
    .map(
      (line) => `<tr>
        <td>${esc(line.code)}</td>
        <td class="r">${money(line.product)}</td>
        <td class="r">${esc(line.quantity)}</td>
        <td class="r">${weight(line.weight)}</td>
        <td class="r">${money(line.cnCourier)}</td>
        <td class="r">${money(line.shipping)}</td>
        <td class="r">${money(line.bdCourier)}</td>
        <td class="r">${money(line.discount)}</td>
        <td class="r">${money(line.total)}</td>
        <td class="r paid">${money(line.paid)}</td>
        <td class="r due">${money(line.due)}</td>
      </tr>`
    )
    .join("");

  return `<section class="invoice">
    <header class="top">
      <div class="company">
        ${logo}
        ${COMPANY.address ? `<div class="muted">${esc(COMPANY.address)}</div>` : ""}
        ${COMPANY.phone ? `<div class="muted">${esc(COMPANY.phone)}</div>` : ""}
        ${COMPANY.email ? `<div class="muted">${esc(COMPANY.email)}</div>` : ""}
      </div>
      <div class="side">
        <div class="meta">
          <div><span>Invoice No</span><strong>${esc(invoice.invoiceNo)}</strong></div>
          <div><span>Total Orders</span><strong>${esc(invoice.orderCount ?? lines.length)}</strong></div>
          <div><span>Delivery</span><strong>${esc(invoice.deliveryMethod || "-")}</strong></div>
        </div>
        <div class="billto">
          <div class="label">BILL TO</div>
          <div class="customer">${esc(invoice.customerName || "-")}</div>
          ${invoice.customerCode ? `<div>${esc(invoice.customerCode)}</div>` : ""}
          ${invoice.customerPhone ? `<div>${esc(invoice.customerPhone)}</div>` : ""}
          ${invoice.customerAddress ? `<div class="muted">${esc(invoice.customerAddress)}</div>` : ""}
          <div class="when">${esc(formatDate(invoice.createdDateTime, "DD-MMM-YYYY hh:mm A"))}</div>
        </div>
      </div>
    </header>

    <h2>Order &amp; Shipment Details</h2>
    <table class="items">
      <thead>
        <tr>
          <th>Order</th><th class="r">Product</th><th class="r">Qty</th><th class="r">Weight</th><th class="r">CN Courier</th>
          <th class="r">Shipping</th><th class="r">BD Courier</th><th class="r">Discount</th><th class="r">Total</th><th class="r">Paid</th><th class="r">Due</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="11" class="c muted">No orders</td></tr>`}</tbody>
      <tfoot>
        <tr class="sum">
          <td>TOTAL</td>
          <td class="r">${money(invoice.productTotal)}</td>
          <td class="r">${esc(invoice.quantity)}</td>
          <td class="r">${weight(invoice.weight)}</td>
          <td class="r">${money(invoice.cnCourier)}</td>
          <td class="r">${money(invoice.shipping)}</td>
          <td class="r">${money(invoice.bdCourier)}</td>
          <td class="r">${money(invoice.discount)}</td>
          <td class="r">${money(invoice.total)}</td>
          <td class="r paid">${money(invoice.paid)}</td>
          <td class="r due">${money(invoice.due)}</td>
        </tr>
      </tfoot>
    </table>

    <div class="signs">
      <div><span></span>Customer Signature</div>
      <div><span></span>Store Manager</div>
      <div><span></span>Accounts</div>
    </div>
    <footer class="foot">Developed By Qodemart</footer>
  </section>`;
};

const STYLE = `
  /* no page margin: the browser prints its page title / date / address only in the margin, so none of it is on the paper */
  @page { size: A4 portrait; margin: 0; }
  .invoice { padding: 10mm; }
  .invoice { page-break-after: always; break-after: page; position: relative; }
  .invoice:last-child { page-break-after: auto; break-after: auto; }
  .muted { color: #6b7280; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 16px; padding-bottom: 12px; }
  .company { flex: 0 0 38%; line-height: 1.5; }
  .logo { height: 42px; max-width: 180px; object-fit: contain; display: block; margin-bottom: 6px; }
  .company-name { font-size: 18px; font-weight: 700; }
  .side { flex: 1; display: flex; gap: 18px; justify-content: flex-end; align-items: flex-start; }
  .meta { min-width: 150px; }
  .meta div { display: flex; justify-content: space-between; gap: 14px; padding: 2px 0; }
  .meta span { color: #6b7280; }
  .meta div:first-child strong { font-size: 13px; }
  .billto { min-width: 150px; max-width: 200px; line-height: 1.5; padding-left: 16px; border-left: 1px solid #108653; }
  .billto .label { font-size: 9px; font-weight: 700; letter-spacing: .8px; color: #6b7280; }
  .billto .when { color: #6b7280; }
  .billto .customer { font-weight: 700; font-size: 12px; }
  h2 { margin: 0 0 8px; font-size: 14px; color: #0b5d3a; padding-bottom: 5px; }
  table.items { table-layout: fixed; margin-bottom: 36px; font-size: 9px; }
  table.items th, table.items td { padding: 5px 3px; word-break: break-word; }
  table.items th { font-size: 8.5px; }
  table.items th:first-child, table.items td:first-child { width: 14%; padding-left: 5px; }
  table.items thead { display: table-header-group; }
  table.items tr { page-break-inside: avoid; }
  table.items th { background: #eaf6f0; color: #0b5d3a; }
  table.items tfoot td { font-weight: 700; background: #f3f4f6; }
  .r { text-align: right; }
  .c { text-align: center; }
  .paid { color: #15803d; }
  .due { color: #dc2626; }
  .signs { display: flex; justify-content: space-between; gap: 30px; margin-top: 60px; page-break-inside: avoid; }
  .signs div { flex: 1; text-align: center; font-size: 11px; color: #374151; }
  .signs span { display: block; border-top: 1px solid #111; margin-bottom: 4px; }
  .foot { margin-top: 30px; text-align: center; font-size: 10px; color: #9ca3af; }
`;

// A4 portrait, no app chrome: printed inside its own frame.
// One invoice per page; the invoices of the list follow each other.
export const printInvoices = (invoices, { title } = {}) => {
  const list = (invoices || []).filter(Boolean);
  if (!list.length) return false;
  const name = title || (list.length === 1 ? `Invoice ${list[0].invoiceNo}` : `Invoices (${list.length})`);
  printDocument(name, list.map(invoiceHtml).join(""), STYLE);
  return true;
};

/**
 * Generate-then-print: the orders get an invoice number (kept for the history), then the invoice prints.
 * `separate` = one invoice per order instead of one invoice for all the orders. Returns the invoices.
 */
export const generateAndPrintInvoices = async (orderIds, { separate = false } = {}) => {
  const groups = separate ? orderIds.map((id) => [id]) : [orderIds];
  const invoices = [];
  for (const group of groups) invoices.push(await generateInvoice(group));
  printInvoices(invoices);
  return invoices;
};
